'use client';

import { useRouter } from 'next/navigation';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { setOnUnauthorized } from '@/apis/app-api';
import { OAUTH_POPUP_CALLBACK_PATH } from '@/consts/google-oauth';
import { GRS_SESSION_STORAGE_KEY, GrsTokenData, grsTokenManager } from '@/lib/grs-token-manager';

import { usePartnerConfig } from './TenantProvider';

// --- Types ---

interface GrsSessionContextValue {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresIn: number;
  refreshTokenExpiresIn: number;
  user: Record<string, any> | null;
  isAuthenticated: boolean;
  isRevalidating: boolean;
  signIn: (tokenData: GrsTokenData) => void;
  signOut: () => void;
  revalidate: () => void;
}

// --- Context ---

const GrsSessionContext = createContext<GrsSessionContextValue>({
  accessToken: '',
  refreshToken: '',
  accessTokenExpiresIn: 0,
  refreshTokenExpiresIn: 0,
  user: null,
  isAuthenticated: false,
  isRevalidating: false,
  signIn: () => {},
  signOut: () => {},
  revalidate: () => {},
});

// --- Provider ---

export function GrsSessionProvider({ children }: { children: React.ReactNode }) {
  const config = usePartnerConfig();
  const isSessionStorage = config.auth.strategy === 'session-storage';
  const router = useRouter();

  const [tokenData, setTokenData] = useState<GrsTokenData | null>(() => grsTokenManager.current);
  const [isRevalidating, setIsRevalidating] = useState(false);

  // Manager → React state 동기화
  useEffect(() => {
    const unsubData = grsTokenManager.subscribe(setTokenData);
    const unsubRevalidating = grsTokenManager.onRevalidating(setIsRevalidating);
    return () => {
      unsubData();
      unsubRevalidating();
    };
  }, []);

  // 세션 만료 시 매직링크 페이지로 이동
  useEffect(() => {
    if (!isSessionStorage) return;
    // tokenData가 null로 바뀌었는데 이전에 인증 상태였으면 → 만료
    if (tokenData === null && !grsTokenManager.isAuthenticated) {
      // signOut에 의한 null은 manager.current도 null
      // 이미 매직링크 페이지에 있으면 무시
      if (window.location.pathname.includes('/auth/lottegrs/magic-link')) return;
      // playground는 public demo라 GRS auth 요구하지 않음
      if (window.location.pathname.startsWith('/playground')) return;
      // 구글 OAuth 팝업 relay는 세션 없는 팝업 창에서 뜬다(iframe과 storage 파티션이 다름).
      // 여기서 만료 리다이렉트를 걸면 부모에게 결과를 넘기기 전에 화면이 날아간다.
      if (window.location.pathname.startsWith(OAUTH_POPUP_CALLBACK_PATH.replace(/\/$/, ''))) return;
      // sessionStorage가 비어있으면 (signOut 호출됨) 리다이렉트
      if (!sessionStorage.getItem(GRS_SESSION_STORAGE_KEY)) {
        router.replace(config.auth.expiredPath);
      }
    }
  }, [tokenData, isSessionStorage, config.auth.expiredPath, router]);

  // 401 인터셉터 연결
  useEffect(() => {
    if (!isSessionStorage) return;
    setOnUnauthorized(grsTokenManager.revalidate);
    return () => setOnUnauthorized(null);
  }, [isSessionStorage]);

  // 이벤트 기반 토큰 갱신 (focus / visibilitychange)
  useEffect(() => {
    if (!isSessionStorage) return;

    grsTokenManager.revalidate();

    const onVisible = () => {
      if (document.visibilityState === 'visible') grsTokenManager.revalidate();
    };
    const onFocus = () => grsTokenManager.revalidate();

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [isSessionStorage]);

  const signIn = useCallback((data: GrsTokenData) => grsTokenManager.signIn(data), []);
  const signOut = useCallback(() => grsTokenManager.signOut(), []);

  const value = useMemo<GrsSessionContextValue>(
    () => ({
      accessToken: tokenData?.accessToken ?? '',
      refreshToken: tokenData?.refreshToken ?? '',
      accessTokenExpiresIn: tokenData?.accessTokenExpiresIn ?? 0,
      refreshTokenExpiresIn: tokenData?.refreshTokenExpiresIn ?? 0,
      user: tokenData?.user ?? null,
      isAuthenticated: !!tokenData?.accessToken,
      isRevalidating,
      signIn,
      signOut,
      revalidate: grsTokenManager.revalidate,
    }),
    [tokenData, isRevalidating, signIn, signOut],
  );

  return <GrsSessionContext.Provider value={value}>{children}</GrsSessionContext.Provider>;
}

export function useGrsSession() {
  return useContext(GrsSessionContext);
}
